import { MoreVertical, FileText, Image, FileIcon, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { KnowledgeDoc } from "@/lib/types";
import { cn } from "@/lib/utils";

interface FileRowProps {
  file: KnowledgeDoc;
  onDelete: () => void;
  isDeleting: boolean;
}

export function FileRow({ file, onDelete, isDeleting }: FileRowProps) {
  const safeType = typeof (file as any)?.type === 'string' ? (file as any).type as string : '';
  const getFileIcon = (type: string) => {
    if (type && type.startsWith("image/")) return Image;
    if (type && type.includes("pdf")) return FileText;
    return FileIcon;
  };

  const formatFileSize = (bytes: number | undefined) => {
    if (!Number.isFinite(bytes as any)) return "";
    if ((bytes as number) === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"] as const;
    const i = Math.floor(Math.log((bytes as number)) / Math.log(k));
    return parseFloat(((bytes as number) / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? "" : d.toLocaleDateString();
  };

  const FileIconComponent = getFileIcon(safeType);

  return (
    <div className={cn(
      "flex items-center gap-3 p-3 rounded-lg bg-ios-surface-secondary",
      "hover:bg-accent transition-colors duration-200"
    )}>
      <div className="shrink-0">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <FileIconComponent size={20} className="text-primary" />
        </div>
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground truncate">
          {file?.name ?? "Untitled"}
        </div>
        <div className="text-xs text-muted-foreground">
          {formatFileSize((file as any)?.size)} • {formatDate((file as any)?.uploadedAt)}
        </div>
      </div>
      
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "shrink-0 h-8 w-8 rounded-full",
              "hover:bg-accent active:scale-95 transition-all duration-200"
            )}
            disabled={isDeleting}
          >
            <MoreVertical size={16} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-32">
          <DropdownMenuItem
            onClick={onDelete}
            disabled={isDeleting}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 size={16} className="mr-2" />
            {isDeleting ? "Deleting..." : "Delete"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}